import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/providers/AuthProvider";

interface Product {
  id: string;
  name: string;
  code: string;
  unit: string;
}

interface OrderItem {
  id: string;
  quantity: number;
  product: Product;
}

interface Department {
  id: string;
  name: string;
  acronym: string;
}

interface Order {
  id: string;
  order_number: string;
  responsible_name: string;
  department: Department;
  construction_name: string;
  created_at: string;
  status: string;
  reject_reason?: string;
  order_items: OrderItem[];
  user_id: string;
}

const statusMap = {
  pending: { label: "Pendente", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Aprovado", color: "bg-green-100 text-green-800" },
  rejected: { label: "Rejeitado", color: "bg-red-100 text-red-800" },
  completed: { label: "Concluído", color: "bg-blue-100 text-blue-800" }
};

export default function UserOrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setIsLoading(true);
      console.log('Buscando detalhes do pedido:', id);

      // Buscar dados básicos do pedido
      const { data: orderData, error } = await supabase
        .from("orders")
        .select(`
          *,
          department:departments (*),
          order_items (
            id,
            quantity,
            product:products (*)
          )
        `)
        .eq("id", id)
        .single();

      if (error) {
        console.error('Erro ao carregar pedido:', error);
        toast({
          title: "Erro ao carregar detalhes do pedido",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      console.log('Dados básicos do pedido:', orderData);

      // Verificar se o pedido pertence ao usuário atual
      if (orderData.user_id !== user?.id) {
        navigate('/orders');
        toast({
          title: "Acesso negado",
          description: "Você não tem permissão para ver este pedido.",
          variant: "destructive",
        });
        return;
      }

      // Transform the data to match the Order interface
      const transformedOrder: Order = {
        id: orderData.id,
        order_number: orderData.order_number,
        responsible_name: orderData.responsible_name,
        construction_name: orderData.construction_name,
        created_at: orderData.created_at,
        status: orderData.status,
        reject_reason: orderData.reject_reason,
        user_id: orderData.user_id,
        department: {
          id: orderData.department.id,
          name: orderData.department.name,
          acronym: orderData.department.acronym,
        },
        order_items: orderData.order_items.map((item: any) => ({
          id: item.id,
          quantity: item.quantity,
          product: {
            id: item.product.id,
            name: item.product.name,
            code: item.product.code,
            unit: item.product.unit,
          },
        })),
      };

      console.log('Pedido formatado:', transformedOrder);
      setOrder(transformedOrder);
    } catch (error: any) {
      console.error("Erro ao buscar detalhes do pedido:", error);
      toast({
        title: "Erro ao carregar detalhes",
        description: error.message || "Ocorreu um erro ao carregar os detalhes do pedido.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Pedido não encontrado</div>
        </div>
      </div>
    );
  }

  const status = statusMap[order.status as keyof typeof statusMap] || statusMap.pending;

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Botão Voltar */}
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate('/my-orders')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Voltar
      </Button>

      {/* Informações Principais */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Pedido #{order.order_number}</CardTitle>
            <Badge className={status.color}>
              {status.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Informações do Pedido</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Solicitante:</span> {order.responsible_name}</p>
                <p><span className="font-medium">Departamento:</span> {order.department.name}</p>
                <p><span className="font-medium">Obra:</span> {order.construction_name}</p>
                <p><span className="font-medium">Data:</span> {new Date(order.created_at).toLocaleDateString('pt-BR')}</p>
                {order.status === 'rejected' && order.reject_reason && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="font-medium text-red-700 mb-1">Motivo da Rejeição:</p>
                    <p className="text-red-600">{order.reject_reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Produtos */}
      <Card>
        <CardHeader>
          <CardTitle>Produtos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">Produto</th>
                  <th scope="col" className="px-6 py-3">Código</th>
                  <th scope="col" className="px-6 py-3">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {order?.order_items?.map((item, index) => (
                  <tr key={index} className="bg-white border-b">
                    <td className="px-6 py-4">{item?.product?.name || 'N/A'}</td>
                    <td className="px-6 py-4">{item?.product?.code || 'N/A'}</td>
                    <td className="px-6 py-4">{item?.quantity || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
